import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { classNames } from "@/lib/utils";
import styles from "./ClampedNote.module.scss";

/**
 * A summary whose length is only known once it arrives, holding one height while it loads and once
 * it is in: on the screens where the parent sets `--note-clamp` (a height) and `--note-toggle: block`,
 * the text is cut to that height and "Show more" opens the rest. A box sized by its text pushed the
 * page below it down when the text landed (CLS); a reader's own click is not a layout shift.
 * Elsewhere both variables are unset and the text takes its natural height, with no toggle.
 */
const ClampedNote: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    // After every render (the text arrives as children) and on resize (the width sets the line count).
    useEffect(() => {
        const element = ref.current;
        if (element) setOverflows(element.scrollHeight > element.clientHeight + 1);
    });
    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => setOverflows(element.scrollHeight > element.clientHeight + 1));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div ref={ref} className={classNames(styles.body, expanded ? styles.expanded : "", className ?? "")}>
                {children}
            </div>
            {/* Its row is always there where the text is cut, so the card does not grow when the toggle is needed. */}
            <button type="button" className={styles.toggle} style={{ visibility: overflows || expanded ? "visible" : "hidden" }} aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
                {expanded ? "Show less" : "Show more"}
            </button>
        </>
    );
};

export default ClampedNote;
